import { withIsolationScope, withScope, startSpan, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, handleCallbackErrors, SPAN_STATUS_ERROR, captureException, setHttpStatus } from '@sentry/core';
import { winterCGHeadersToDict, propagationContextFromHeaders } from '@sentry/utils';
import { isRedirectNavigationError, isNotFoundNavigationError } from './nextNavigationErrorUtils.js';
import { flushSafelyWithTimeout } from './utils/responseEnd.js';
import { escapeNextjsTracing, commonObjectToIsolationScope, commonObjectToPropagationContext } from './utils/tracingUtils.js';
import { vercelWaitUntil } from './utils/vercelWaitUntil.js';

/**
 * Wraps a Next.js route handler with performance and error instrumentation.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function wrapRouteHandlerWithSentry(
  routeHandler,
  context,
) {
  const { method, parameterizedRoute, headers } = context;

  return new Proxy(routeHandler, {
    apply: (originalFunction, thisArg, args) => {
      return escapeNextjsTracing(() => {
        const isolationScope = commonObjectToIsolationScope(headers);

        const completeHeadersDict = headers ? winterCGHeadersToDict(headers) : {};

        isolationScope.setSDKProcessingMetadata({
          request: {
            headers: completeHeadersDict,
          },
        });

        const incomingPropagationContext = propagationContextFromHeaders(
          completeHeadersDict['sentry-trace'],
          completeHeadersDict['baggage'],
        );

        const propagationContext = commonObjectToPropagationContext(headers, incomingPropagationContext);

        return withIsolationScope(isolationScope, () => {
          return withScope(async scope => {
            scope.setTransactionName(`${method} ${parameterizedRoute}`);
            scope.setPropagationContext(propagationContext);
            try {
              return startSpan(
                {
                  name: `${method} ${parameterizedRoute}`,
                  attributes: {
                    [SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'route',
                    [SEMANTIC_ATTRIBUTE_SENTRY_OP]: 'http.server',
                    [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.function.nextjs',
                  },
                  forceTransaction: true,
                },
                async span => {
                  const response = await handleCallbackErrors(
                    () => originalFunction.apply(thisArg, args),
                    error => {
                      // Next.js throws errors when calling `redirect()`. We don't wanna report these.
                      if (isRedirectNavigationError(error)) {
                        // Don't do anything
                      } else if (isNotFoundNavigationError(error) && span) {
                        span.setStatus({ code: SPAN_STATUS_ERROR, message: 'not_found' });
                      } else {
                        captureException(error, {
                          mechanism: {
                            handled: false,
                          },
                        });
                      }
                    },
                  );

                  try {
                    if (span && response.status) {
                      setHttpStatus(span, response.status);
                    }
                  } catch (e) {
                    // best effort - response may be undefined?
                  }

                  return response;
                },
              );
            } finally {
              vercelWaitUntil(flushSafelyWithTimeout());
            }
          });
        });
      });
    },
  });
}

export { wrapRouteHandlerWithSentry };
//# sourceMappingURL=wrapRouteHandlerWithSentry.js.map
